import { useState } from 'react';
import userEvaluationApi from 'Api/axios/user-evaluation-api';
import { UserEvaluationI } from 'types';

interface SelectedCourse {
  courseId: string;
  courseName: string;
}

/**
 * Manages the lazy-loaded "evaluation details" modal for a course row in the
 * employee courses report. Fetching is triggered on demand by openModal.
 *
 * @param employeeId - user whose evaluations are fetched (the report subject)
 */
const useEmployeeCourseEvaluations = (employeeId: string) => {
  const [open, setOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<SelectedCourse | null>(null);
  const [evaluations, setEvaluations] = useState<UserEvaluationI[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openModal = async (courseId: string, courseName: string) => {
    setSelectedCourse({ courseId, courseName });
    setEvaluations([]);
    setError(null);
    setLoading(true);
    setOpen(true);

    const response = await userEvaluationApi.getUserEvaluationsByCourseId(courseId, employeeId);

    if (response.error) {
      setError(response.error);
      setEvaluations([]);
    } else {
      setEvaluations(response.data ?? []);
    }

    setLoading(false);
  };

  const closeModal = () => {
    setOpen(false);
    setSelectedCourse(null);
    setEvaluations([]);
    setError(null);
    setLoading(false);
  };

  return {
    open,
    selectedCourse,
    evaluations,
    loading,
    error,
    openModal,
    closeModal,
  };
};

export default useEmployeeCourseEvaluations;
